import { gql } from "@apollo/client";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import apollo, { API_CMS_PROVIDER } from "@/helpers/apollo";
import Search from "@/pages/search";

export const exploreSearchSchema = z.object({
  boundingBox: z.string().optional(),
  categories: z.string().optional(),
  coupons: z.string().optional(),
  difficulty: z.string().optional(),
  duration: z.string().optional(),
  has_bookings: z.string().optional(),
  include: z.string().optional(),
  is_scary: z.string().optional(),
  kind: z.string().optional(),
  location: z.string().optional(),
  page: z.coerce.number().optional(),
  players: z.string().optional(),
  privacy: z.string().optional(),
  query: z.string().optional(),
  sortBy: z.string().optional(),
  status: z.string().optional(),
  subcategories: z.string().optional(),
  xmas_tag: z.string().optional(),
});

export type ExploreSearchParams = z.infer<typeof exploreSearchSchema>;

const fetchAttractionCount = createServerFn({ method: "GET" }).handler(
  async () => {
    const client = apollo();
    const query = gql`
      {
        homepage {
          data {
            attributes {
              ctaFactoids {
                title
              }
            }
          }
        }
      }
    `;

    const response = await client.query({
      context: { clientName: API_CMS_PROVIDER },
      query,
    });

    return {
      attractionCount:
        response.data.homepage.data.attributes.ctaFactoids[0].title,
    };
  }
);

export const Route = createFileRoute("/explore/")({
  component: ExplorePage,
  head: ({ loaderData, match }) => {
    if (!loaderData) {
      return {};
    }
    const { attractionCount } = loaderData;
    const query = ((match.search as ExploreSearchParams).query || "").trim();
    const title = query ? `${query} escape rooms` : "Escape rooms";
    const docTitle = `${title} - Morty: Every. Escape room. Ever.`;
    const description = `Dive into ${attractionCount} epic attractions! Explore a wide array of immersive adventures to find your next thrilling experience and join the excitement now.`;

    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(
      match.search as Record<string, unknown>
    )) {
      if (value !== undefined && value !== null) {
        searchParams.set(key, String(value));
      }
    }
    const searchStr = searchParams.toString();
    const href = `/explore/${searchStr ? `?${searchStr}` : ""}`;

    return {
      links: [{ href: `${process.env.APP_URL}${href}`, rel: "canonical" }],
      meta: [
        { title: docTitle },
        { content: description, name: "description" },
        { content: description, property: "og:description" },
        {
          content: `${process.env.APP_URL}/images/share.jpg`,
          property: "og:image",
        },
        { content: docTitle, property: "og:title" },
        { content: "website", property: "og:type" },
        { content: `${process.env.APP_URL}${href}`, property: "og:url" },
        { content: "summary_large_image", name: "twitter:card" },
        { content: "@mortyapp", name: "twitter:site" },
      ],
    };
  },
  headers: () => ({
    "Cache-Control": "s-maxage=3600, stale-while-revalidate=86400",
  }),
  loader: () => fetchAttractionCount(),
  validateSearch: exploreSearchSchema,
});

function ExplorePage() {
  return <Search />;
}
